import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Phone, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { LOGO_RAIZES_WHITE } from '../../lib/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CadastroViaConvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    celular: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    checkInvite();
  }, [token]);

  const checkInvite = async () => {
    try {
      const res = await axios.get(`${API}/invite/${token}`);
      setInviteInfo(res.data);
    } catch (error) {
      console.error('Erro ao verificar convite:', error);
      toast.error('Convite inválido ou expirado');
    } finally {
      setCheckingInvite(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.celular || !formData.password) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 4) {
      toast.error('A senha deve ter pelo menos 4 caracteres');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/parent/self-register`, {
        nome: formData.nome,
        celular: formData.celular,
        password: formData.password,
        invite_token: token
      });
      
      // Salvar token e dados
      localStorage.setItem('parent_token', res.data.token);
      localStorage.setItem('parent_data', JSON.stringify({
        parent_id: res.data.parent_id,
        parent_name: res.data.parent_name,
        child_id: res.data.child_id
      }));
      
      toast.success('Cadastro realizado com sucesso!');
      navigate('/app-pais/dashboard');
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao realizar cadastro');
    } finally {
      setLoading(false);
    }
  };

  if (checkingInvite) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Verificando convite...</p>
      </div>
    );
  }

  if (!inviteInfo) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4">
        <Card className="bg-white rounded-2xl p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">❌</div>
          <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-2">
            Convite Inválido
          </h2>
          <p className="font-inter text-gray-600 mb-6">
            Este link de convite não é válido ou já expirou.
          </p>
          <Button
            onClick={() => navigate('/app-pais/login')}
            className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8 py-3"
          >
            Ir para Login
          </Button>
        </Card>
      </div>
    );
  }

  if (inviteInfo.already_registered) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4">
        <Card className="bg-white rounded-2xl p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-2">
            Já Cadastrado
          </h2>
          <p className="font-inter text-gray-600 mb-6">
            Já existe um responsável cadastrado para <strong>{inviteInfo.child_name}</strong>.
          </p>
          <Button
            onClick={() => navigate('/app-pais/login')}
            className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8 py-3"
          >
            Fazer Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A4D2E] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-8">
          <img 
            src={LOGO_RAIZES_WHITE}
            alt="Ministério Raízes"
            className="h-20 mx-auto mb-4 drop-shadow-xl"
          />
          <h1 className="font-nunito font-bold text-3xl text-white mb-1">
            Ministério Raízes
          </h1>
          <p className="font-inter text-white/70">
            Cadastro de Responsável
          </p>
          <p className="font-inter text-[#F4D35E] font-semibold mt-2">
            Para: {inviteInfo.child_name}
          </p>
        </div>

        <Card className="bg-white rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="nome" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nome do Responsável
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="nome"
                  name="nome"
                  data-testid="input-nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Ex: Maria Silva"
                  className="pl-11 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="celular" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Celular
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="celular"
                  name="celular"
                  type="tel"
                  data-testid="input-celular"
                  value={formData.celular}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  className="pl-11 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="input-password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 4 caracteres"
                  className="pl-11 pr-10 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Confirmar Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  data-testid="input-confirm-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repita a senha"
                  className="pl-11 pr-10 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-confirm">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all"
            >
              {loading ? 'Cadastrando...' : 'Completar Cadastro'}
            </Button>

            <p className="text-center font-inter text-sm text-gray-600 mt-4">
              Após o cadastro, você poderá acompanhar e registrar as atividades diárias.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CadastroViaConvite;
